import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const params = new URLSearchParams(window.location.search);
  const key = params.get("key") || "";
  const [state, setState] = useState({ loading: true, error: null, items: [] });
  const [selected, setSelected] = useState(null);

  // Refetch whenever the admin key changes; other deps captured through closures intentionally.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!key) {
      setState({ loading: false, error: "Missing ?key=... in URL", items: [] });
      return;
    }
    axios
      .get(`${API}/admin/submissions?key=${encodeURIComponent(key)}`)
      .then((r) => setState({ loading: false, error: null, items: r.data.items }))
      .catch((e) => setState({ loading: false, error: e?.response?.data?.detail || "Failed to load", items: [] }));
  }, [key]);

  const openDetail = async (id) => {
    setSelected({ id, loading: true });
    try {
      const r = await axios.get(`${API}/admin/submissions/${id}?key=${encodeURIComponent(key)}`);
      setSelected({ id, loading: false, data: r.data });
    } catch (e) {
      setSelected({ id, loading: false, error: e?.response?.data?.detail || "Failed" });
    }
  };

  if (state.loading) return <div className="p-16 text-center overline">Loading…</div>;
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="stage">
          <div className="overline mb-4">Admin</div>
          <h1 className="font-serif text-4xl font-light">{state.error}</h1>
          <p className="mt-4 text-[#555]">Append <span className="font-mono">?key=YOUR_ADMIN_KEY</span> to the URL.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="flex items-center justify-between px-6 md:px-12 py-8 border-b border-[#E5E5E5]">
        <div className="overline">Sainte Noire — Admin</div>
        <div className="overline">{state.items.length} Submissions</div>
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        <div className="overline mb-6">All submissions (newest first)</div>
        <div className="border border-[#E5E5E5]">
          <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-[#E5E5E5] text-xs uppercase tracking-widest text-[#737373]">
            <div className="col-span-3">Business</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-2">Primary Gap</div>
            <div className="col-span-2">Prescription</div>
            <div className="col-span-1">Lead</div>
            <div className="col-span-1 text-right">Created</div>
          </div>
          {state.items.map((it) => (
            <button
              key={it.id}
              onClick={() => openDetail(it.id)}
              data-testid={`admin-row-${it.id}`}
              className="w-full grid grid-cols-12 gap-4 px-4 py-4 border-b border-[#E5E5E5] hover:bg-[#F3F3F3] text-left text-sm"
            >
              <div className="col-span-3">
                <div className="font-medium">{it.business_name}</div>
                <div className="text-xs text-[#737373]">{it.name}</div>
              </div>
              <div className="col-span-3 truncate">{it.email}</div>
              <div className="col-span-2">{it.primary_gap}</div>
              <div className="col-span-2">{it.prescription || "—"}</div>
              <div className="col-span-1">
                {it.discovery_submitted ? (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#9fa5a3] text-white" data-testid={`lead-badge-${it.id}`}>
                    Lead
                  </span>
                ) : (
                  <span className="text-xs text-[#bbb]">—</span>
                )}
              </div>
              <div className="col-span-1 text-right text-xs text-[#737373]">
                {it.created_at?.slice(0, 16).replace("T", " ")}
              </div>
            </button>
          ))}
          {state.items.length === 0 && (
            <div className="p-8 text-center text-[#737373]">No submissions yet.</div>
          )}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div className="overline">Submission Detail</div>
              <button className="overline" onClick={() => setSelected(null)}>Close</button>
            </div>
            <div className="p-6 text-sm">
              {selected.loading && <div className="overline">Loading…</div>}
              {selected.error && <div className="text-[#9fa5a3]">{selected.error}</div>}
              {selected.data && (
                <pre className="whitespace-pre-wrap break-words text-xs leading-relaxed">
                  {JSON.stringify(selected.data, null, 2)}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
